import Resolution from 'engine/Resolution.js';
import {getStepsInSelection} from "pattern/utils/getStepsInSelection.js"
import {deleteStepFromGroup} from 'pattern/groups/stepGroups.js';
import {DELETE_STEP} from './constants.js';
import StepBucket from './StepBucket.js';

const baseResolution = new Resolution(32, false);
const baseTripletResolution = new Resolution(32, true);

class EasyPattern {
    constructor(pattern, trackData) {
        this.pattern = pattern;
        this.trackData = trackData;
    }

    getStep(stepNumber, resolution, transpose) {
        let checker = (stepData) =>
            stepData.transpose === null && (transpose === this.trackData.transpose) ||
            stepData.transpose === transpose;
        return this.pattern.getStepAtResolution(stepNumber, resolution, checker);
    }

    getStepsAtStepNumber(stepNumber, resolution=baseResolution) {
        return this.pattern.getPolyphonicStepsAtResolution(stepNumber, resolution);
    }

    getStepWithTranspose(stepNumber, resolution, transpose, position=0) {
        let checker = (stepData) =>
            stepData.transpose === null && (transpose === this.trackData.transpose) ||
            Math.round(stepData.transpose) === transpose;

        let filtered = this.pattern.getAllOnStepAtResolutionAtAnyPosition(stepNumber, resolution, position)
            .filter(s => s.on && checker(s));
        if (filtered.length > 0) {
            return filtered[0];
        }
        return undefined;
    }

    deleteStepsAtStepNumber(stepNumber, resolution, update=true) {
        // TODO: say you're at 1/8t and delete that specific step bucket
        // well, there are still a couple other step buckets to delete!
        if (resolution.isTriplet) {
            stepNumber = baseTripletResolution 
                .transformStepAtResolution2(stepNumber, resolution);
            this.pattern.tripletStepBuckets[stepNumber] = new StepBucket(this.pattern.track, true);
        } else {
            stepNumber = baseResolution
                .transformStepAtResolution2(stepNumber, resolution);
            this.pattern.stepBuckets[stepNumber] = new StepBucket(this.pattern.track);
        }

        if (update) {
            this.update(resolution.isTriplet);
        }
    }

    deleteStepAtStepNumber(stepData, stepNumber, resolution, update=true) {
        stepNumber = this.getStepNumber(stepNumber, resolution);
        let stepBucket = resolution.isTriplet ?
            this.pattern.tripletStepBuckets[stepNumber] :
            this.pattern.stepBuckets[stepNumber];
        if (stepBucket === undefined) {
            return;
        }

        for (let position in stepBucket.steps) {
            let currentStepData = stepBucket.steps[position];
            let headStepData = currentStepData.deleteStep(stepData);
            if (headStepData === DELETE_STEP) {
                delete stepBucket.steps[position] //headStepData;
                break;
            } else if (headStepData !== undefined &&
                       headStepData !== currentStepData) {
                stepBucket.steps[position] = headStepData;
                break;
            }
        }

        if (!stepBucket.anyOn()) {
            stepBucket.clear();
        }

        if (update) {
            this.update(resolution.isTriplet);
        }
    }

    // finds the step and deletes it
    deleteStep(stepData, update=true) {
        // look for the step
        let stepBuckets = this.pattern.stepBucketsMatrix;
        deleteStepFromGroup(stepData);

        for (let i in stepBuckets) {
            let stepBucket = stepBuckets[i];
            if (!stepBucket) {
                continue;
            }
            for (let position in stepBucket.steps) {
                let currentStepData = stepBucket.steps[position];
                let headStepData = currentStepData.deleteStep(stepData);
                if (headStepData === DELETE_STEP) {
                    // then we actually deleted it
                    delete stepBucket.steps[position];
                    break;
                } else if (headStepData !== undefined) {
                    stepBucket.steps[position] = headStepData;
                    break;
                }
            }

            if (!stepBucket.anyOn()) {
                stepBucket.clear();
            }
        }
        if (update) {
            this.update();
        }
    }

    addStep(stepData, stepNumber, resolution, position=0, update=true, skipCheck=false, isRecording=false) {
        stepData.track = this.pattern.track;
        let transformedStepNumber =  baseResolution
            .transformStepAtResolution2(stepNumber, resolution);
        if (isRecording && stepNumber >= this.pattern.length(resolution)) {
            this.pattern.changeLength(transformedStepNumber + 2 * stepData.duration, false);
        }

        let existingStep  = this.pattern.getStepAtResolution(
            stepNumber, resolution, () => true, position);

        if (existingStep !== undefined && !skipCheck) {
            stepData = existingStep.addStep(stepData);
        }
        
        this.pattern.setStepAtResolution(stepData, stepNumber, resolution, position);
        if (update) {
            if (resolution.isTriplet) {
                this.update(resolution.isTriplet);
            } else {
                let transformedStepNumber = baseResolution
                    .transformStepAtResolution2(stepNumber, resolution);
                this.pattern.stepBucketsMatrix[transformedStepNumber] =
                    this.pattern.stepBuckets[transformedStepNumber];
                this.pattern.needsSync = true;
            }
        }

        return stepData;
    }

    getSelectedSteps(selection, resolution) {
        let steps = [];
        for (let i=selection.fromStep ; i <= selection.toStep; i++) {
            let currentSteps = this.pattern.getAllOnStepAtResolutionAtAnyPosition(i, resolution);
            steps = steps.concat(currentSteps);
        }

        return steps;
    }

    getAllIntersectingSteps(stepNumber, resolution, previous=0, isFirst) {
        let steps = [];
        for (let i=stepNumber; i >= previous; i--) {
            let _stepNumber = Math.floor(i);
            let currentSteps = this.pattern.getAllOnStepAtResolutionAtAnyPosition(
                _stepNumber, resolution);
            for (let step of currentSteps) {
                let duration = step.duration;
                if (_stepNumber + duration > (isFirst ? stepNumber : Math.floor(stepNumber))) {
                    steps.push(step);
                }
            }
        }
        return steps;
    }

    getSelectedPianoRollSteps(selection, resolution, checkIntersection=false) {
        if (checkIntersection) {
            return getStepsInSelection({
                pattern: this.pattern,
                selection,
                resolution,
            });
        }
        let steps = [];
        let previous = undefined;
        let end = selection.stepNumber + selection.numSteps;
        let isFirst=true;
        for (let i=selection.stepNumber; i <= selection.stepNumber + selection.numSteps ; i++) {
            let currentSteps = checkIntersection ? this.getAllIntersectingSteps(i, resolution, previous, isFirst) :
                this.pattern.getAllOnStepAtResolutionAtAnyPosition(i, resolution);
            isFirst = false;
            let filteredSteps = currentSteps
                .filter(step => this.isStepSelected(step, selection));
            steps = steps.concat(filteredSteps);
            previous = i;
        }

        if (checkIntersection && Math.floor(previous) < Math.floor(end)) {
            // check the last step then
            // need to be BEFORE end position
            let _end = baseResolution.transformStepAtResolution2(end, resolution);
            let currentSteps = this.pattern.getAllOnStepAtResolutionBeforePosition(
                Math.floor(_end),
                baseResolution,
                _end - Math.floor(_end));
            let filteredSteps = currentSteps
                .filter(step => this.isStepSelected(step, selection));
            steps = steps.concat(filteredSteps);
        }

        return Array.from(new Set(steps));
    }

    getStepNumber(stepNumber, resolution) {
        return resolution.isTriplet ?
            baseTripletResolution.transformStepAtResolution2(stepNumber, resolution) :
            baseResolution.transformStepAtResolution2(stepNumber, resolution);
    }

    isStepSelected(step, selection) {
        let transpose = step.transpose === null ?
            this.trackData.transpose : step.transpose;
        return transpose <= selection.transpose &&
            transpose > selection.transpose - selection.numNotes;
    }

    getStepNumbers(steps, resolution, includePosition=false) {
        // for each step find out where its located
        let stepToStepNumber = [];
        for (let i in steps) {
            let step = steps[i];
            let stepNumber = this.findStepNumber(step, resolution, includePosition);
            stepToStepNumber.push([step, stepNumber]);
        }

        return stepToStepNumber;
    }

    // moves the selected steps, using the transformation obj as the direction
    // of the movement
    transformSteps(steps, stepToStepNumber, transformation, resolution, wrap=false, update=true) {
        // for each step find out where its located
        let a = new Date().getTime();
        if (transformation.stepDifference === 0 &&
            transformation.transposeDifference === 0) {
            return steps;
        }
        let hasZeroes = stepToStepNumber.some(
            x =>
                x[1] + x[0].position + transformation.stepDifference <= 0);
        if (wrap && hasZeroes && transformation.stepDifference < 0) {
            return steps;
        }

        for (let i in stepToStepNumber) {
            let step = stepToStepNumber[i][0];
            let stepNumber = stepToStepNumber[i][1];
            this.deleteStepAtStepNumber(step, stepNumber, resolution, false);
        }

        let newSteps = [];
        let mapping = {};
        let patternLength = this.pattern.length(resolution);
        for (let i in stepToStepNumber) {
            let step = stepToStepNumber[i][0];
            let stepNumber = stepToStepNumber[i][1];
            let original = stepNumber;
            let transpose = step.transpose === null ?
                this.trackData.transpose :
                step.transpose;

            if (isNaN(transformation.stepDifference)) {
                transformation.stepDifference = 0;
            }

            // calculate the transformation
            step.transpose = transpose + transformation.transposeDifference;
            let toAdd = step.position + transformation.stepDifference;
            stepNumber += toAdd; 

            if (isNaN(stepNumber)) {
                continue;
            }  else if (stepNumber < 0) {
                if (wrap) {
                    stepNumber = 0;
                    step.position = 0;
                } else {
                    stepNumber = patternLength + stepNumber;;
                } 
            } else if (wrap) {
                stepNumber = stepNumber % patternLength;
            }

            // calculate the new position
            let newStepNumber = Math.floor(stepNumber);
            let newPosition = (stepNumber - newStepNumber);
            // calculate based on ratio between resolution/32
            step.position = newPosition;

            if (original !== newStepNumber) {
                mapping[original] = newStepNumber;
            }
            this.addStep(step, newStepNumber, resolution, newPosition, false);
            newSteps.push(step);
        }

        if (update && !resolution.isTriplet) {
            this.pattern.clearCache();
            this.pattern.syncMatrixBySteps(mapping, resolution);
        } else if (update) {
            this.update(resolution.isTriplet);
        }
        return newSteps;
    }

    snapToGrid(steps, resolution) {
        let stepToStepNumber = this.getStepNumbers(steps, resolution);

        for (let i in stepToStepNumber) {
            let step = stepToStepNumber[i][0];
            let stepNumber = stepToStepNumber[i][1];
            let position = step.position;
            this.deleteStepAtStepNumber(step, stepNumber, resolution, false);
        }

        let isTriplet = resolution.isTriplet;
        for (let i in stepToStepNumber) {
            let step = stepToStepNumber[i][0];
            let stepNumber = Math.round(stepToStepNumber[i][1]);
            step.position = 0;
            this.addStep(step, stepNumber, resolution, 0, false);
        }

        this.update(isTriplet);
    }

    findStepNumber(stepData, resolution=baseResolution, includePosition=false) {
        let stepBuckets = resolution.isTriplet ?
            this.pattern.tripletStepBucketsMatrix :
            this.pattern.stepBucketsMatrix;
        for (let i in stepBuckets) {
            let stepBucket = stepBuckets[i];
            if (stepBucket === undefined) {
                continue;
            }
            for (let position in stepBucket.steps) {
                let currentStepData = stepBucket.steps[position];
                if (currentStepData.hasStep(stepData)) {
                    // then we actually deleted it
                    return resolution.transformStepAtResolution2(
                        includePosition ? parseFloat(i) + parseFloat(position) : i,
                        resolution.isTriplet ?
                            baseTripletResolution :
                            baseResolution);
                }
            }
        }

        return undefined;
    }

    findStepNumberAndDelete(stepData, resolution, update=false) {
        let stepBuckets = this.pattern.stepBucketsMatrix;
        for (let i in stepBuckets) {
            let stepBucket = stepBuckets[i];
            for (let position in stepBucket.steps) {
                let currentStepData = stepBucket.steps[position];
                let headStepData = currentStepData.deleteStep(stepData);
                if (headStepData !== undefined) {
                    // then we actually deleted it
                    stepBucket.steps[position] = headStepData;
                    if (update) {
                        this.update(resolution.isTriplet);
                    }
                    return resolution.transformStepAtResolution2(
                        i,
                        resolution.isTriplet ?
                            baseTripletResolution :
                            baseResolution);
                }
            }
        }

        return undefined;
    }

    deleteSteps(steps) {
        steps.forEach(step => this.deleteStep(step, false, undefined, true));
        this.update();
    }

    getAllSteps() {
        let steps = [];
        let stepBuckets = this.pattern.stepBuckets;
        for (let i in stepBuckets) {
            let stepBucket = stepBuckets[i];
            for (let position in stepBucket.steps) {
                let currentStepData = stepBucket.steps[position];
                steps = steps.concat(currentStepData.getPolyphony()
                    .filter(x => x.on));
            }
        }

        return steps;
    }

    pasteSteps(steps, selection, easyPatternToCopy, resolution) {
        if (steps === undefined || selection === undefined) {
            return;
        }
        let stepToStepNumber = easyPatternToCopy.getStepNumbers(steps, resolution);
        let minStepNumber = 60000;
        for (let i in stepToStepNumber) {
            let stepNumber = stepToStepNumber[i][1];
            if (stepNumber < minStepNumber) {
                minStepNumber = stepNumber;
            }
        }

        for (let i in stepToStepNumber) {
            stepToStepNumber[i][1] -= minStepNumber;
        }

        let selectionStep = selection.stepNumber;

        let pastedSteps = [];
        for (let i in stepToStepNumber) {
            let step = stepToStepNumber[i][0].getCopy(
                true, false, false, true, false, false);
            let stepNumber = stepToStepNumber[i][1];
            let transformedStepNumber = selectionStep + stepNumber;
            pastedSteps.push(this.addStep(
                step, transformedStepNumber, resolution, step.position, false));
        }

        this.update(resolution.isTriplet);

        return pastedSteps;
    }

    getFirstStepNumber(steps, resolution) {
        let minStepNumber = Infinity;
        let minStep = undefined;
        for (let [stepData, stepNumber] of this.getStepNumbers(steps, resolution)) {
            if (stepNumber < minStepNumber) {
                minStepNumber = stepNumber;
                minStep = stepData;
            }
        }
        return minStepNumber;
    }

    getFirstOnStep(resolution) {
        let step = 0;
        for (let bucket of this.pattern.stepBuckets) {
            if (bucket.getAnyOnStep()) {
                return resolution.transformStepAtResolution2(
                    step, baseResolution);
            }
            step++;
        }
        return 0;
    }

    update(isTriplet=false) {
        this.pattern.calculateFills(this.trackData);
        this.pattern.clearCache();
        this.pattern.syncBuckets(isTriplet);
    }
}

export default EasyPattern;
